import { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import '../styles/chatbot.css';

function buildWelcome(buddy) {
  if (!buddy) {
    return "Ask me anything about your BidBoard projects or Procore jobs.\n\u2022 How many bids are we tracking?\n\u2022 Show me active jobs in California\n\u2022 What is my win rate?";
  }
  return `Hi, I'm ${buddy.name}, ${buddy.type ? `the ${buddy.type}` : 'your buddy'}. Ask me about your BidBoard projects or Procore jobs.\n\u2022 How many bids are we tracking?\n\u2022 Show me active jobs in California\n\u2022 What is my win rate?`;
}

export default function ChatBot({ buddy, onClose, onAsk }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState(() => [{
    role: 'assistant',
    content: buildWelcome(buddy),
    sql: '',
    data: [],
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    document.body.classList.toggle('chatbot-panel-open', isExpanded);
    return () => document.body.classList.remove('chatbot-panel-open');
  }, [isExpanded]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Build the full conversation history to send to the server. Only user + assistant
  // text turns are included; the welcome message is UI-only and excluded so it doesn't
  // pollute Claude's context.
  function buildHistoryForServer(nextUserTurn) {
    const prior = messages
      .filter(m => m.role === 'user' || (m.role === 'assistant' && m !== messages[0]))
      .map(m => ({ role: m.role, content: m.content }));
    return [...prior, { role: 'user', content: nextUserTurn }];
  }

  const sendMessage = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;

    const history = buildHistoryForServer(trimmed);
    const userMessage = { role: 'user', content: trimmed, sql: '', data: [] };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    if (onAsk) onAsk();

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          messages: history,                   // full conversation history — enables follow-ups
          buddyName: buddy?.name || null,
          buddyBio: buddy?.bio || null,
        }),
      });
      const data = await res.json();

      if (data.error) {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.error, sql: '', data: [] }]);
      } else {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: data.answer || data.content || data.message || '',
          sql: data.sql || '',
          data: data.data || [],
        }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.', sql: '', data: [] }]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetConversation = () => {
    setMessages([{ role: 'assistant', content: buildWelcome(buddy), sql: '', data: [] }]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const titleName = buddy ? buddy.name : 'BidBoard AI';
  const subtitle = buddy ? `The ${buddy.type}` : 'Ask about BidBoard + Procore data';

  return (
    <div className={`chatbot-window ${isExpanded ? 'chatbot-expanded' : ''}`}>
      <div className="chatbot-header">
        {buddy && (
          <pre className="chatbot-header-sprite" aria-hidden="true">{buddy.ascii}</pre>
        )}
        <div style={{ flex: 1 }}>
          <div className="chatbot-header-title">{titleName}</div>
          <div className="chatbot-header-subtitle">{subtitle}</div>
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button className="chatbot-close" onClick={resetConversation} aria-label="New conversation" title="Start a new conversation (clears history)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
          <button className="chatbot-close" onClick={() => setIsExpanded(e => !e)} aria-label={isExpanded ? 'Minimize' : 'Expand'} title={isExpanded ? 'Minimize' : 'Expand to sidebar'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {isExpanded ? (
                <><path d="M4 14h6v6"/><path d="M20 10h-6V4"/><path d="M14 10l7-7"/><path d="M3 21l7-7"/></>
              ) : (
                <><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></>
              )}
            </svg>
          </button>
          <button className="chatbot-close" onClick={onClose} aria-label="Close chat">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <div className="chatbot-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`chatbot-msg chatbot-msg-${msg.role}`}>
            <div className={`chatbot-bubble chatbot-bubble-${msg.role}`}>
              {msg.role === 'assistant' ? (
                <div className="chatbot-markdown"><Markdown>{msg.content || '(No response)'}</Markdown></div>
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
              )}
              {msg.sql && <SqlCollapsible sql={msg.sql} />}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="chatbot-msg chatbot-msg-assistant">
            <div className="chatbot-bubble chatbot-bubble-assistant">
              <div className="chatbot-typing">
                <span className="chatbot-dot" />
                <span className="chatbot-dot" />
                <span className="chatbot-dot" />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chatbot-input-area">
        <input
          ref={inputRef}
          className="chatbot-input"
          type="text"
          placeholder="Type a message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <button
          className="chatbot-send"
          onClick={sendMessage}
          disabled={isLoading || !inputValue.trim()}
          aria-label="Send message"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function SqlCollapsible({ sql }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="chatbot-sql-section">
      <button className="chatbot-sql-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? '\u25BC' : '\u25B6'} View query
      </button>
      {open && <pre className="chatbot-sql-code">{sql}</pre>}
    </div>
  );
}
